'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import type { AuthoritativeTerminalPaintAck } from './market-share-liveline-frame-source';
interface AuthoritativeTerminalHandoffInput<T> {
  readonly authoritativeMode: boolean;
  readonly enabled: boolean;
  readonly locked: boolean;
  readonly sessionBaseKey: string;
  readonly lockedControlKey: string;
  readonly targetChartReady: boolean;
  readonly liveValue: T;
}
export interface AuthoritativeTerminalHandoff<T> {
  readonly displayValue: T;
  readonly holdingTerminal: boolean;
  readonly sessionKey: string | undefined;
  readonly sessionGeneration: number | undefined;
  readonly onTerminalPainted: (ack: AuthoritativeTerminalPaintAck) => void;
}
interface LiveSession {
  readonly phase: 'live';
  readonly baseKey: string;
  readonly generation: number;
  readonly terminalGeometryRevision: string | null;
}
interface TerminalSession {
  readonly phase: 'terminal';
  readonly baseKey: string;
  readonly generation: number;
  readonly terminalGeometryRevision: string | null;
  readonly lockedControlKey: string;
  readonly targetChartReady: boolean;
}
type Session = LiveSession | TerminalSession;
interface HandoffState<T> {
  readonly retainedLiveValue: T;
  readonly nextGeneration: number;
  readonly session: Session | null;
}
export function useAuthoritativeTerminalHandoff(input) {
  const $ = _c(11);
  let t0;
  if ($[0] !== input) {
    t0 = () => initialState(input);
    $[0] = input;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [state, setState] = useState(t0);
  let t1;
  if ($[2] !== input || $[3] !== state) {
    t1 = nextState(state, input);
    $[2] = input;
    $[3] = state;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const next = t1;
  if (next !== state) {
    setState(next);
  }
  const session = next.session;
  const holdingTerminal = session?.phase === "terminal";
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ack => {
      setState(current => acknowledgeTerminalPaint(current, ack));
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const onTerminalPainted = t2;
  const t3 = holdingTerminal ? next.retainedLiveValue : input.liveValue;
  const t4 = session === null ? undefined : `${session.baseKey}:${String(session.generation)}`;
  const t5 = session?.generation;
  let t6;
  if ($[6] !== holdingTerminal || $[7] !== t3 || $[8] !== t4 || $[9] !== t5) {
    t6 = {
      displayValue: t3,
      holdingTerminal,
      sessionKey: t4,
      sessionGeneration: t5,
      onTerminalPainted
    };
    $[6] = holdingTerminal;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}
function initialState<T>(input: AuthoritativeTerminalHandoffInput<T>): HandoffState<T> {
  if (!input.authoritativeMode || !input.enabled) {
    return {
      retainedLiveValue: input.liveValue,
      nextGeneration: 1,
      session: null
    };
  }
  return {
    retainedLiveValue: input.liveValue,
    nextGeneration: 2,
    session: {
      phase: 'live',
      baseKey: input.sessionBaseKey,
      generation: 1,
      terminalGeometryRevision: null
    }
  };
}
function nextState<T>(current: HandoffState<T>, input: AuthoritativeTerminalHandoffInput<T>): HandoffState<T> {
  if (!input.authoritativeMode) return withoutSession(current);
  if (input.enabled) return retainLiveSession(current, input);
  if (!input.locked) return withoutSession(current);
  const session = current.session;
  if (session === null || session.baseKey !== input.sessionBaseKey) {
    return withoutSession(current);
  }
  if (session.phase === 'live') {
    return {
      ...current,
      session: {
        phase: 'terminal',
        baseKey: session.baseKey,
        generation: session.generation,
        terminalGeometryRevision: session.terminalGeometryRevision,
        lockedControlKey: input.lockedControlKey,
        targetChartReady: input.targetChartReady
      }
    };
  }
  if (session.lockedControlKey !== input.lockedControlKey || input.targetChartReady && session.terminalGeometryRevision !== null) {
    return withoutSession(current);
  }
  if (session.targetChartReady === input.targetChartReady) return current;
  return {
    ...current,
    session: {
      ...session,
      targetChartReady: input.targetChartReady
    }
  };
}
function retainLiveSession<T>(current: HandoffState<T>, input: AuthoritativeTerminalHandoffInput<T>): HandoffState<T> {
  const session = current.session;
  if (session?.phase === 'live' && session.baseKey === input.sessionBaseKey) {
    return Object.is(current.retainedLiveValue, input.liveValue) ? current : {
      ...current,
      retainedLiveValue: input.liveValue
    };
  }
  return {
    retainedLiveValue: input.liveValue,
    nextGeneration: current.nextGeneration + 1,
    session: {
      phase: 'live',
      baseKey: input.sessionBaseKey,
      generation: current.nextGeneration,
      terminalGeometryRevision: null
    }
  };
}
function acknowledgeTerminalPaint<T>(current: HandoffState<T>, ack: AuthoritativeTerminalPaintAck): HandoffState<T> {
  const session = current.session;
  if (session === null || `${session.baseKey}:${String(session.generation)}` !== ack.sessionKey || session.terminalGeometryRevision !== null) {
    return current;
  }
  if (session.phase === 'terminal' && session.targetChartReady) return withoutSession(current);
  return {
    ...current,
    session: {
      ...session,
      terminalGeometryRevision: ack.terminalGeometryRevision
    }
  };
}
function withoutSession<T>(current: HandoffState<T>): HandoffState<T> {
  return current.session === null ? current : {
    ...current,
    session: null
  };
}